import { type ForgetLevel, whatToForget } from "./forgetRules.ts";
import { persister, QUERY_CACHE_KEY, queryClient } from "./queryClient.ts";
import { supabase } from "./supabase.ts";

const LAST_UID_KEY = "sk-last-uid";
/** Cache d'images du service worker (vite.config.ts). */
const IMAGE_CACHE = "sk-images";

const read = (key: string) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

async function forget(level: ForgetLevel) {
  if (level === "nothing") return;
  // Lectures : en mémoire, puis leur copie sur le téléphone.
  queryClient.clear();
  await Promise.resolve(persister.removeClient()).catch(() => undefined);
  try {
    localStorage.removeItem(QUERY_CACHE_KEY);
  } catch {
    // Stockage indisponible (navigation privée) : rien n'y était gardé.
  }
  if ("caches" in window) await caches.delete(IMAGE_CACHE).catch(() => false);
  if (level === "everything") {
    // Base hors ligne chargée à la demande : hors du budget JS initial.
    const { db } = await import("../offline/db.ts");
    await Promise.all([db.outbox.clear(), db.matchEvents.clear()]).catch(() => undefined);
    // Ce téléphone ne doit plus recevoir les notifications du compte qui s'en va.
    const registration = await navigator.serviceWorker?.getRegistration().catch(() => undefined);
    const subscription = await registration?.pushManager?.getSubscription().catch(() => null);
    await subscription?.unsubscribe().catch(() => false);
  }
}

/** À chaque changement de session : compare avec le dernier compte vu sur ce téléphone. */
export function onSessionUser(newUid: string | null) {
  const level = whatToForget(read(LAST_UID_KEY), newUid, false);
  try {
    if (newUid) localStorage.setItem(LAST_UID_KEY, newUid);
    else if (level !== "nothing") localStorage.removeItem(LAST_UID_KEY);
  } catch {
    // Stockage indisponible : on ne peut pas se souvenir du compte, donc rien à comparer.
  }
  void forget(level);
}

/** Bouton « Se déconnecter » : le téléphone oublie tout avant de fermer la session. */
export async function signOutAndForget() {
  // Tant que la session est ouverte, l'adresse de ce téléphone est aussi retirée de la base.
  await import("./push.ts").then((m) => m.disableThisDevice()).catch(() => undefined);
  await forget("everything");
  try {
    localStorage.removeItem(LAST_UID_KEY);
  } catch {
    // Voir plus haut.
  }
  await supabase.auth.signOut();
}
